"use client";

import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { createArea, deleteArea, updateArea } from "../../api/area";
import customFetch from "hooks/fetchWrapper";
import ConfirmationDialog from "hooks/ConfirmationDialog";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import * as XLSX from "xlsx";

const AreaTab = () => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [newArea, setNewArea] = useState({ code: "", eid: "" });
  const [emirates, setEmirates] = useState([]);

  // Excel menu state
  const [excelMenuAnchorEl, setExcelMenuAnchorEl] = useState(null);
  const excelMenuOpen = Boolean(excelMenuAnchorEl);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAreas();
    fetchEmirates();
  }, []);

  const fetchAreas = async () => {
    try {
      const data = await customFetch("areas");
      if (data.status && Array.isArray(data.data)) {
        setRows(data.data);
      } else {
        console.error("Unexpected response format:", data);
      }
    } catch (error) {
      console.error("Error fetching areas:", error);
    }
  };

  const fetchEmirates = async () => {
    try {
      const data = await customFetch("emirates");
      setEmirates(data);
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setNewArea({ code: "", eid: "" });
  };

  const handleSubmitNewArea = async () => {
    if (newArea.code && newArea.eid) {
      try {
        const response = await createArea(newArea);
        if (response.status && response.data) {
          const newAreaData = Array.isArray(response.data) ? response.data[0] : response.data;
          const formattedNewArea = {
            ...newAreaData,
            emirateName: emirates.find((emirate) => emirate.eid === newAreaData.eid)?.name,
          };
          setRows((prevRows) => [...prevRows, formattedNewArea]);
          handleCloseModal();
        } else {
          console.error("Unexpected response format:", response);
        }
      } catch (error) {
        console.error("Error creating area:", error);
      }
    }
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    setSelectedRowId(id);
    setOpenConfirmDialog(true);
  };

  const handleDialogClose = (confirmed) => {
    setOpenConfirmDialog(false);
    if (confirmed) {
      handleConfirmDelete();
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteArea(selectedRowId);
      setRows(rows.filter((row) => row.aid !== selectedRowId));
    } catch (error) {
      console.error("Error deleting area:", error);
    }
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    updateArea(newRow.aid, updatedRow).then(() => {
      setRows(rows.map((row) => (row.aid === newRow.aid ? updatedRow : row)));
    });
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  // Excel menu handlers
  const handleExcelButtonClick = (event) => {
    setExcelMenuAnchorEl(event.currentTarget);
  };

  const handleExcelMenuClose = () => {
    setExcelMenuAnchorEl(null);
  };

  // Download template handler
  const handleDownloadTemplate = () => {
    // Create a workbook with a single sheet
    const wb = XLSX.utils.book_new();

    // Create a worksheet with header rows
    const wsData = [["code", "eid"]];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, "Areas");

    // Generate a filename with date and time
    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-");
    const fileName = `areas_template_${dateStr}_${timeStr}.xlsx`;

    // Write the workbook and trigger download
    XLSX.writeFile(wb, fileName);

    handleExcelMenuClose();
  };

  // File upload handlers
  const handleUploadClick = () => {
    fileInputRef.current.click();
    handleExcelMenuClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const data = await readExcelFile(file);
      if (data && data.length > 0) {
        // Process the data and create areas
        await bulkCreateAreas(data);
      }
    } catch (error) {
      console.error("Error processing Excel file:", error);
    }

    // Reset the file input
    event.target.value = null;
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "array" });

          // Get the first worksheet
          const worksheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[worksheetName];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Validate data structure
          const validData = jsonData.filter((item) => {
            // Ensure code is a string and eid is a number
            const isValid =
              item.code &&
              (typeof item.code === "string" || typeof item.code === "number") &&
              item.eid &&
              !isNaN(Number(item.eid));

            // Convert code to string and eid to number if valid
            if (isValid) {
              item.code = String(item.code);
              item.eid = Number(item.eid);
            }

            return isValid;
          });

          resolve(validData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const bulkCreateAreas = async (areasData) => {
    try {
      // Create an array of promises for each area creation
      const creationPromises = areasData.map((area) =>
        createArea({
          code: area.code,
          eid: area.eid,
        })
      );

      // Wait for all promises to resolve
      const results = await Promise.all(creationPromises);

      // Process results and update the UI
      const newAreas = results
        .filter((response) => response.status && response.data)
        .map((response) => {
          const areaData = Array.isArray(response.data) ? response.data[0] : response.data;
          return {
            ...areaData,
            emirateName: emirates.find((emirate) => emirate.eid === areaData.eid)?.name,
          };
        });

      if (newAreas.length > 0) {
        setRows((prevRows) => [...prevRows, ...newAreas]);
      }
    } catch (error) {
      console.error("Error creating areas in bulk:", error);
    }
  };

  const columns = [
    // { field: 'aid', headerName: 'AID', width: 100 },
    { field: "code", headerName: "Code", width: 120, editable: true },
    { field: "emirateName", headerName: "Emirate", flex: 1, minWidth: 150 },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 130,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key="save"
              icon={
                <Button variant="text" sx={{ minWidth: 20, padding: "0 0px" }}>
                  Save
                </Button>
              }
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key="cancel"
              icon={
                <Button variant="text" sx={{ minWidth: 40, padding: "0 0px" }}>
                  Cancel
                </Button>
              }
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box sx={{ height: 500, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExcelButtonClick}
          startIcon={<BackupTableIcon />}
        >
          Excel
        </Button>
        <Menu anchorEl={excelMenuAnchorEl} open={excelMenuOpen} onClose={handleExcelMenuClose}>
          <MenuItem onClick={handleDownloadTemplate}>
            <ListItemIcon>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Template</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleUploadClick}>
            <ListItemIcon>
              <FileUploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Upload File</ListItemText>
          </MenuItem>
        </Menu>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx, .xls"
          style={{ display: "none" }}
        />
        <Button variant="contained" onClick={handleOpenModal}>
          Add New Area
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        getRowId={(row) => row.aid}
      />
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-area-modal"
        aria-describedby="modal-to-add-new-area"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <h2 id="add-area-modal">Add New Area</h2>
          <TextField
            autoFocus
            margin="dense"
            id="code"
            label="Area Code"
            type="text"
            fullWidth
            variant="standard"
            value={newArea.code}
            onChange={(e) => setNewArea({ ...newArea, code: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="emirate-select-label">Emirate</InputLabel>
            <Select
              labelId="emirate-select-label"
              id="emirate-select"
              value={newArea.eid}
              label="Emirate"
              onChange={(e) => setNewArea({ ...newArea, eid: e.target.value })}
            >
              {emirates?.map((emirate) => (
                <MenuItem key={emirate.eid} value={emirate.eid}>
                  {emirate.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleCloseModal} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button onClick={handleSubmitNewArea} variant="contained">
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>
      <ConfirmationDialog
        open={openConfirmDialog}
        title="Confirm Deletion"
        description="Are you sure you want to delete this area? This action cannot be undone."
        onClose={handleDialogClose}
        onConfirm={() => handleDialogClose(true)}
      />
    </Box>
  );
};

export default AreaTab;
